import { useState, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { usePlacesWidget } from "react-google-autocomplete";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { v4 as uuidv4 } from 'uuid';

import { laboratorySchema } from "../../yup";
import { Input, ImageInput } from "../../components/UserInput";
import {
  useUpdateLabDetailsMutation,
  useGetLaboratoryDetailsQuery,
  useFetchS3ObjectMutation,
} from "../../services/api";
import style from "./laboratory.module.css";
import { Button, LabelBox } from "../../components";
import { appConfig } from "../../hooks/useConfig";
import { colors } from "../../theme/colors";
import labLogo from "../../assets/images/lab-logo.jpg";
import bannerLogo from "../../assets/images/lab-banner.png";
import { getExtension } from "../../utils/common";
import { uploadToS3 } from "../../services/common";

type FormState = typeof laboratorySchema.__outputType;

const EditLaboratory = () => {
  const [updateLaboratory] = useUpdateLabDetailsMutation();
  const [fetchS3Object] = useFetchS3ObjectMutation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [error, setError] = useState(false);
  const [deliveryModeError, setDeliveryModeError] = useState(false);
  const [bannerError, setBannerError] = useState(false);
  const [logoError, setLogoError] = useState(false)
  const [isSavingLaboratory, setIsSavingLaboratory] = useState(false);
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);
  const [faxNumberSelected, setFaxNumberSelected] = useState(false);
  const [emailSelected, setEmailSelected] = useState(false);
  const [banner, setBanner] = useState<File>();
  const [logo, setLogo] = useState<File>();

  // Get Laboratory Details mutation hook
  const {
    data: laboratoryDetails,
    isLoading,
    isFetching,
  } = useGetLaboratoryDetailsQuery(id ?? "");

  const [message, setMessage] = useState<LabelMessageType | null>(null);

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(laboratorySchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
    clearErrors,
  } = useForm<FormState>({
    resolver: yupResolver(laboratorySchema),
    mode: "onChange",
  });

  const { ref: materialRef } = usePlacesWidget({
    apiKey: appConfig?.GOOGLE_API_KEY,
    inputAutocompleteValue: "off",
    options: {
      types: [
        "street_address",
        "street_number",
        "intersection",
        "route",
        "health",
      ],
      componentRestrictions: { country: "au" },
    },
    onPlaceSelected: (place) => {
      let suburb = "";
      let state = "";
      let postal = "";
      let streetName = '';
      let streetNumber = '';
      if (place) {
        clearErrors("postcode");
        clearErrors("suburb");
        clearErrors("state");
        clearErrors('street_number')
        clearErrors('street_name');
        const address_comp = place?.address_components;
        if (address_comp) {
          for (const c of address_comp) {
            const types: Array<string> = c.types;
            if (
              !suburb &&
              (types.includes("locality") ||
                types.includes("sublocality") ||
                types.includes("sublocality_level_1") ||
                types.includes("postal_town") ||
                types.includes("administrative_area_level_3"))
            ) {
              suburb = c.short_name;
            }

            if (types.includes("administrative_area_level_1")) {
              state = c.short_name;
            } else if (types.includes("postal_code")) {
              postal = c.short_name;
            }
            if (types.includes('street_number')) {
              streetNumber = c.short_name;
            }
            if (types.includes('route')) {
              streetName = c.short_name;
            }
          }
          if (streetName === '' || streetNumber === '') {
            const match = place?.formatted_address?.match(/^(\d+)\s+(.+?),/);
            if (match) {
              streetNumber = match[1];
              streetName = match[2];
            } else {
              const address_arr = place?.formatted_address?.split(', ') ?? [];
              if (address_arr?.length > 3) {
                streetName = address_arr?.slice(0, 2).join(', ');
              } else {
                streetName = address_arr?.[0];
              }
            }
          }
          setValue("state", state);
          setValue("suburb", suburb);
          setValue("postcode", postal);
          setValue('street_number', streetNumber)
          setValue('street_name', streetName);
          const lat: any = get(place, "geometry.location.lat", () => { });
          const lng: any = get(place, "geometry.location.lng", () => { });
          setLatitude(lat() ?? 0);
          setLongitude(lng() ?? 0);
        }
      }
    },
  });

  useEffect(() => {
    if (!isEmpty(laboratoryDetails)) {
      reset({
        name: laboratoryDetails?.name ?? "",
        phone:
          laboratoryDetails?.address?.phone ?? "",
        account_manager_mail:
          laboratoryDetails?.account_manager_mail ?? "",
        fax: laboratoryDetails?.address?.fax ?? "",
        mail: laboratoryDetails?.address?.mail ?? "",
        postcode: laboratoryDetails?.address?.postcode,
        state: laboratoryDetails?.address?.state,
        suburb: laboratoryDetails?.address?.suburb,
        street_number: laboratoryDetails?.address?.street_number,
        street_name: laboratoryDetails?.address?.street_name,
        unit_number: laboratoryDetails?.address?.unit_number
      });
      setEmailSelected(!!laboratoryDetails?.address?.is_mail_delivery);
      setFaxNumberSelected(!!laboratoryDetails?.address?.is_fax_delivery);
      setLatitude(laboratoryDetails?.address?.latitude ?? 0);
      setLongitude(laboratoryDetails?.address?.longitude ?? 0);
    }
  }, [laboratoryDetails, reset]);

  const onSubmit = async (values: FormState) => {
    try {
      const {
        fax,
        mail,
        is_fax_delivery,
        is_mail_delivery,
        phone,
        name,
        ...rest
      } = values;

      if (
        typeof laboratoryDetails?.banner_pre_signed_url !== "string" &&
        banner === undefined
      ) {
        setBannerError(true);
        return;
      }
      if (
        typeof laboratoryDetails?.logo_pre_signed_url !== "string" &&
        logo === undefined
      ) {
        setLogoError(true);
        return;
      }
      if (mail === "" && fax === "") {
        setDeliveryModeError(true);
        return;
      }
      if (!faxNumberSelected && !emailSelected) {
        setError(true);
        return;
      }
      setIsSavingLaboratory(true);
      let payload: EditLaboratoryPayload = {
        ...rest,
        id: parseInt(id ?? "0"),
        phone:
          phone?.replace(/ /g, ""),
        fax: fax?.replace(/ /g, ""),
        mail: mail ?? "",
        is_fax_delivery: is_fax_delivery ?? false,
        is_mail_delivery: is_mail_delivery ?? false,
        latitude,
        longitude,
        name: name ?? "",
      };

      if (banner) {
        const fileExtension = getExtension(banner?.name ?? "");
        const keyName = (laboratoryDetails?.banner_path && laboratoryDetails?.name === name) ? laboratoryDetails?.banner_path : `static/laboratory/lab_${name}/banner_${uuidv4()}.${fileExtension}`
        const response = await fetchS3Object({ s3_key_name: keyName }).unwrap();
        if (response?.pre_signed_url) {
          const uploadResponse = await uploadToS3({ url: response?.pre_signed_url, file: banner })
          if (uploadResponse) {
            payload = { ...payload, banner_path: keyName };
          }
        }
      }
      if (logo) {
        const fileExtension = getExtension(logo?.name ?? "");
        const keyName = (laboratoryDetails?.logo_path && laboratoryDetails?.name === name) ? laboratoryDetails?.logo_path : `static/laboratory/lab_${name}/logo_${uuidv4()}.${fileExtension}`
        const response = await fetchS3Object({ s3_key_name: keyName }).unwrap();
        if (response?.pre_signed_url) {
          const uploadResponse = await uploadToS3({ url: response?.pre_signed_url, file: logo })
          if (uploadResponse) {
            payload = { ...payload, logo_path: keyName };
          }
        }
      }
      await updateLaboratory(payload).unwrap();
      setIsSavingLaboratory(false);
      navigate(-1);
    } catch (error: any) {
      // RTK Query wraps API error responses as { data, status }, so the Lambda
      // validation message lives at error.data.detail (matching AddLaboratory,
      // AddHealthOrganisation and EditHealthOrganisation).
      setMessage({
        type: "error",
        text: error?.data?.detail?.toString() || "Please enter valid details",
      });
      setIsSavingLaboratory(false);
    }
  };

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        {(isFetching || isLoading) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
              width: "calc(100vw - 400px)",
              zIndex: 999,
              backgroundColor: "transparent",
              position: "absolute",
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
        <Box>
          <Typography
            className={style.detailHeading2}
            sx={{ marginTop: "10px" }}
          >
            Laboratory Details
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              marginBottom: 1,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 340,
                left: 380,
                zIndex: 10,
              }}
            >
              <ImageInput
                label="Logo"
                register={register}
                name="logo"
                error={logoError}
                onImageSelected={(images) => {
                  setLogo(images[0]);
                }}
                height={"100px"}
                width={"120px"}
                defaultValue={laboratoryDetails?.logo_pre_signed_url ?? labLogo}
              />
            </Box>
            <Box sx={{ width: "100%" }}>
              <ImageInput
                label="Banner"
                required
                register={register}
                name="banner"
                error={bannerError}
                onImageSelected={(images) => {
                  setBannerError(false);
                  setBanner(images[0]);
                }}
                height={"250px"}
                width={"100%"}
                defaultValue={
                  laboratoryDetails?.banner_pre_signed_url ?? bannerLogo
                }
              />
            </Box>
          </Box>
          <Box className={style.flexTop}>
            <Box className={style.row}>
              <Input
                placeholder="Enter name"
                label="Laboratory Name"
                required
                register={register}
                name="name"
                errors={errors}
              />
            </Box>
            <Box className={style.row}>
              <Input
                placeholder="Enter unit number"
                label="Unit Number"
                register={register}
                name="unit_number"
                errors={errors}
              />
              <Input
                placeholder="Enter street number"
                label="Street Number"
                required
                register={register}
                name="street_number"
                errors={errors}
                inputRef={materialRef}
              />
            </Box>
            <Box className={style.row}>
              <Input
                placeholder="Enter street name"
                label="Street name"
                required
                register={register}
                name="street_name"
                errors={errors}
              />
              <Input
                placeholder="Enter suburb"
                label="Suburb"
                required
                register={register}
                name="suburb"
                errors={errors}
              />
            </Box>
            <Box className={style.row}>
              <Input
                placeholder="Enter state"
                label="State"
                required
                register={register}
                name="state"
                errors={errors}
              />
              <Input
                placeholder="Enter postcode"
                label="Postcode"
                required
                register={register}
                name="postcode"
                errors={errors}
              />
            </Box>
            <Typography className={style.detailHeading}>
              Contact Details
            </Typography>
            <Box className={style.flexBottom}>
              <Box className={style.row}>
                <Input
                  placeholder="Enter phone number"
                  label="Phone Number"
                  required
                  register={register}
                  name="phone"
                  errors={errors}
                />
                <Input
                  placeholder="Enter accounts manager email address"
                  label="Email Address (Accounts Manager)"
                  required
                  register={register}
                  name="account_manager_mail"
                  errors={errors}
                />
              </Box>
              <Typography className={style.detailHeading}>
                Request Form Delivery Details (Medical Records)
              </Typography>
              <Box className={style.row}>
                <Input
                  placeholder="Enter email address"
                  label="Email"
                  register={register}
                  name="mail"
                  errors={errors}
                  onFocus={() => {
                    setDeliveryModeError(false);
                  }}
                  onChange={(event) => {
                    setValue("mail", event.target.value);
                    if (event.target.value === "") {
                      setEmailSelected(false);
                    }
                  }}
                />
                <Input
                  placeholder="Enter fax number"
                  label="Fax Number"
                  register={register}
                  name="fax"
                  errors={errors}
                  onFocus={() => {
                    setDeliveryModeError(false);
                  }}
                  onChange={(event) => {
                    setValue("fax", event.target.value, {
                      shouldValidate: true,
                    });
                    clearErrors("fax");
                    if (event.target.value === "") {
                      setFaxNumberSelected(false);
                    }
                  }}
                />
              </Box>
              {deliveryModeError && (
                <Typography sx={{ color: colors.red, fontSize: "12px" }}>
                  * Please provide either Email or Fax Number.
                </Typography>
              )}
              <Typography className={style.detailHeading}>
                Mode of Delivery
              </Typography>
              <Box sx={{ marginLeft: 1 }}>
                <FormControlLabel
                  sx={{ "& span": { py: 0, px: 0.3 } }}
                  control={
                    <Checkbox
                      {...register("is_mail_delivery")}
                      sx={{ color: error ? colors.red : colors.black }}
                      onChange={() => {
                        setError(false);
                        setEmailSelected(!emailSelected);
                      }}
                      checked={emailSelected}
                    />
                  }
                  label="Email"
                  disabled={!watch("mail")}
                />
                <FormControlLabel
                  sx={{ "& span": { py: 0, px: 0.3 } }}
                  control={
                    <Checkbox
                      {...register("is_fax_delivery")}
                      sx={{ color: error ? colors.red : colors.black }}
                      onChange={() => {
                        setError(false);
                        setFaxNumberSelected(!faxNumberSelected);
                      }}
                      checked={faxNumberSelected}
                    />
                  }
                  label="Fax Number"
                  disabled={!watch("fax")}
                />
              </Box>
              {error && (
                <Typography sx={{ color: colors.red, fontSize: "12px" }}>
                  * Please select atleast one mode of delivery
                </Typography>
              )}
            </Box>
          </Box>
          {message && (
            <Box mt={2}>
              <LabelBox
                boxProps={{
                  height: 50,
                  display: "flex",
                  alignItems: "center",
                }}
                data={message}
                onClose={() => setMessage(null)}
              />
            </Box>
          )}
          <Box className={style.buttons}>
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: "transparent" },
              }}
              variant="outlined"
              onClick={() => navigate(-1)}
              label={"Cancel"}
            />
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: colors.primary },
              }}
              variant="contained"
              type="submit"
              label={"Save"}
              loading={isSavingLaboratory}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EditLaboratory;
